import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { Phone, Save, Info } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/** Admin page — configure the Missed-Call Text-Back feature.
 * Stores the auto-reply message + forwarding config in Mongo `app_settings`.
 * The actual Twilio conditional-forwarding wire-up happens in Twilio's console
 * per client (link a subaccount to this config). */
export default function MissedCallConfig() {
  const [msg, setMsg] = useState("Hey! We missed your call — reply here and we'll book you in. What can we help with?");
  const [replyDelay, setReplyDelay] = useState(60);
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/missed-call-config").then(({ data }) => {
      if (data.reply_text) setMsg(data.reply_text);
      if (data.reply_delay_sec) setReplyDelay(data.reply_delay_sec);
      if (typeof data.enabled === "boolean") setEnabled(data.enabled);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/missed-call-config", {
        reply_text: msg,
        reply_delay_sec: replyDelay,
        enabled,
      });
      toast.success("Missed-call config saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-8" data-testid="missed-call-config-page">
      <Toaster position="top-right" richColors />
      <h1 className="font-display text-3xl font-bold flex items-center gap-3 mb-2">
        <Phone className="w-7 h-7 text-[#E24E1B]" /> Missed-Call Text-Back
      </h1>
      <p className="text-sm text-neutral-600 mb-8 max-w-2xl">
        Add-on offering ($97/mo per client). When a call comes in and isn't answered within 25 sec,
        Twilio auto-forwards to voicemail AND fires this SMS to the caller within 60 seconds.
        ~12% of missed calls re-engage this way.
      </p>

      <div className="bg-white border border-neutral-200 p-6 mb-6">
        <label className="flex items-center gap-2 mb-4">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="w-4 h-4" />
          <span className="font-semibold text-sm">Enable text-back for all configured clients</span>
        </label>
        <div className="mb-4">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1.5">Auto-reply message (max 160 chars)</div>
          <textarea
            value={msg} onChange={(e) => setMsg(e.target.value.slice(0, 160))}
            rows={3}
            className="w-full px-3 py-3 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black font-mono"
            data-testid="missed-call-message"
          />
          <div className="text-xs text-neutral-500 mt-1">{msg.length} / 160 characters</div>
        </div>
        <div className="mb-4">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1.5">Reply delay (seconds)</div>
          <input
            type="number" min={0} max={600}
            value={replyDelay}
            onChange={(e) => setReplyDelay(Math.max(0, Math.min(600, parseInt(e.target.value || "0", 10))))}
            className="w-32 px-3 py-2 border border-neutral-300 bg-white text-sm focus:outline-none focus:border-black font-mono"
          />
        </div>
        <button onClick={save} disabled={saving} className="lf-btn-primary inline-flex items-center gap-2">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 p-4 flex items-start gap-2 text-sm text-amber-900">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-700" />
        <div>
          <strong>Setup per client:</strong> in Twilio, create a subaccount for each client and enable conditional
          forwarding to <code className="bg-white px-1">TWILIO_WEBHOOK_URL/api/twilio/missed-call</code>.
          The webhook is auto-registered when this config is saved — clients just port their number.
        </div>
      </div>
    </div>
  );
}
